// client/src/context/SocketContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) throw new Error('useSocket must be used within SocketProvider');
  return context;
};

export const SocketProvider = ({ children, userId }) => {
  const [socket, setSocket] = useState(null);
  const [pendingMatchData, setPendingMatchData] = useState(null);
  const [pendingFinishedMatch, setPendingFinishedMatch] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  
  // ========== منع تكرار resume-match ==========
  const lastResumedMatchIdRef = useRef(null);
  
  // ========== منع تكرار match-finished ==========
  const lastResultRef = useRef(null);

  const forceReconnect = useCallback(() => {
    if (socketRef.current && !socketRef.current.connected) {
      //console.log('🔄 Force reconnecting...');
      socketRef.current.disconnect();
      socketRef.current.connect();
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      console.warn('⚠️ [SocketContext] No userId provided');
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    //console.log(`🔌 Initializing socket for user ${userId}`);

    const newSocket = io('https://api.soccerup.games', {
      withCredentials: true,
      transports: ['polling'],           // استخدام polling فقط - تجنب WebSocket
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 3000,
      randomizationFactor: 0.3,
      timeout: 8000,
      forceNew: true,
      autoConnect: true,
    });

    newSocket.on('connect', () => {
      //console.log('✅ Connected, transport:', newSocket.io.engine?.transport?.name);
      newSocket.emit('user-connected', userId);
      newSocket.emit('rejoin-match');
      
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    });

    newSocket.on('connect_error', (err) => {
      console.error('❌ Connection error:', err.message);
    });

    newSocket.on('disconnect', (reason) => {
      console.warn(`⚠️ Disconnected: ${reason}`);
      
      if (reason === 'io server disconnect') {
        setTimeout(() => {
          if (newSocket) newSocket.connect();
        }, 500);
      }
      
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = setTimeout(() => {
        if (newSocket && !newSocket.connected) {
          //console.log('🔄 Reconnecting after delay...');
          newSocket.connect();
        }
      }, 5000);
    });

    newSocket.on('reconnect_attempt', (attempt) => {
      //console.log(`🔄 Reconnect attempt #${attempt}`);
    });

    newSocket.on('reconnect', (attempt) => {
      //console.log(`✅ Reconnected after ${attempt} attempts`);
      newSocket.emit('user-connected', userId);
      newSocket.emit('rejoin-match');
    });

    newSocket.on('reconnect_error', (err) => {
      console.error('❌ Reconnect error:', err.message);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed');
    });

    // فحص صحة الاتصال كل 10 ثوانٍ
    heartbeatIntervalRef.current = setInterval(() => {
      if (newSocket && newSocket.connected) {
        newSocket.emit('ping');
      } else if (newSocket && !newSocket.connected && navigator.onLine) {
        //console.log('🔄 Heartbeat: reconnecting...');
        newSocket.connect();
      }
    }, 10000);

    newSocket.on('pong', () => {});

    // ========== معالجة resume-match - منع التكرار السريع (500ms) ==========
    newSocket.on('resume-match', (data) => {
      // منع تكرار معالجة نفس المباراة (قد يأتي حدثين متتاليين)
      if (lastResumedMatchIdRef.current === data.matchId) {
        //console.log('⏩ [SocketContext] Ignoring duplicate resume-match for match', data.matchId);
        return;
      }
      lastResumedMatchIdRef.current = data.matchId;
      // ✅ NEW: إعادة تعيين المرجع بعد 500ms فقط بدلاً من 3000ms
      setTimeout(() => {
        if (lastResumedMatchIdRef.current === data.matchId) {
          lastResumedMatchIdRef.current = null;
        }
      }, 500);

      //console.log('📦 Received resume-match', data.matchId);
      setPendingMatchData(data);
    });

    newSocket.on('match-start', (data) => {
      //console.log('🎮 Received match-start', data.matchId);
      setPendingMatchData(data);
    });

    // ========== منع تكرار match-finished ==========
    newSocket.on('match-finished', (data) => {
      //console.log('🏁 Received match-finished');
      
      // منع تكرار استلام نفس النتيجة بناءً على المحتوى
      const resultKey = data.result ? `${data.result.yourScore}-${data.result.opponentScore}` : '';
      if (resultKey && lastResultRef.current === resultKey) {
       // console.log('⏩ [SocketContext] Ignoring duplicate match-finished result');
        return;
      }
      if (resultKey) lastResultRef.current = resultKey;
      // إعادة تعيين المرجع بعد 5 ثوانٍ (في حالة بدء مباراة جديدة)
      setTimeout(() => {
        lastResultRef.current = null;
      }, 5000);

      setPendingFinishedMatch(data);
    });

    newSocket.on('match-error', (error) => {
      console.error('Match error:', error);
    });

    newSocket.on('search-cancelled', () => {
     // console.log('Search cancelled');
    });

    newSocket.on('waiting-for-opponent', () => {
     // console.log('Waiting for opponent');
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
    //  console.log('🧹 Cleaning up socket');
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      lastResumedMatchIdRef.current = null;
      lastResultRef.current = null;
    };
  }, [userId]);

  const consumePendingMatch = () => {
    const data = pendingMatchData;
    if (data) setPendingMatchData(null);
    return data;
  };

  const consumePendingFinishedMatch = () => {
    const data = pendingFinishedMatch;
    if (data) setPendingFinishedMatch(null);
    return data;
  };

  return (
    <SocketContext.Provider 
      value={{ 
        socket: socketRef.current, 
        consumePendingMatch, 
        consumePendingFinishedMatch,
        forceReconnect 
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};